import { createContext, useContext, useState, useRef } from 'react';
import { SteppedRange } from '../SteppedRange/SteppedRange';
import { Stack } from 'react-bootstrap';
import { useAnalytics } from 'use-analytics';
import React, { useEffect } from 'react';
import { calcStepIndex, findTargetIndex, getVisibleSteps, isHiddenStep, stripStepMarker } from './wizardNavigation';

type WizardSteps = { [key: string]: React.ReactNode | React.ReactNode[] };

interface WizardContextProps<T extends WizardSteps> {
  getCurrentStep: () => keyof T;
  goToStep: (step: keyof T) => void;
  nextStep: () => void;
  previousStep: () => void;
  getStepData: () => React.ReactNode;
  getTotalSteps: () => number;
  getCurrentStepIndex: () => number;
  isFirstStep: () => boolean;
  goToFirstStep: () => void;
  goToLastStep: () => void;
  isLastStep: () => boolean;
  /**
   * For a step that finds out at runtime it has nothing to show (e.g. no Prove business matches): leave
   * in the direction the applicant arrived from. Arriving via Back it continues backward, so Back is
   * never bounced forward again; with nothing earlier to go back to it falls forward like nextStep().
   */
  skipStep: () => void;
  markStepLoaded: (step: string) => void;
  stepOrder: Array<keyof T>;
}

const WizardContext = createContext<WizardContextProps<any> | undefined>(undefined);

const getVisibleStepOrderMap = (stepOrder: Array<string | number>, stepOrderOffset = 0, hiddenSteps: readonly string[] = []) => {
  const visibleSteps = getVisibleSteps(stepOrder, hiddenSteps).map((s) => stripStepMarker(String(s)));
  return Object.fromEntries(visibleSteps.map((stepName, idx) => [stepName, idx + 1 + stepOrderOffset]));
};

export function useWizard<T extends WizardSteps>(initialStep: keyof T | undefined, steps: T, stepOrder: Array<keyof T>, onStepChange: () => void = () => {}, emitStepAnalytics = false, flowType?: string, stepOrderOffset = 0, manualStepLoaded: string[] = [], hiddenSteps: string[] = []) {
  const analytics = useAnalytics();
  if (!analytics) {
  throw new Error('Analytics context is missing. Did you forget to wrap your app in <AnalyticsProvider>?');
}
  const startingStep = initialStep ?? stepOrder[0]
  const [currentStep, setCurrentStep] = useState<keyof T>(initialStep ?? stepOrder[0]);
  const hasTrackedFirstStep = useRef(false);
  const loadedSteps = useRef(new Set<string>());
  // Which way the applicant last moved; read by skipStep() so a self-skipping step keeps going that way.
  const arrivalDirection = useRef<'forward' | 'backward'>('forward');
  const markStepLoaded = (step: string) => { loadedSteps.current.add(step); };
    useEffect(() => {
      if (!hasTrackedFirstStep.current) {
        analytics.track('FirstStepLoaded', {
        fromStep: startingStep,
        allPlannedSteps: stepOrder,
        allPossibleSteps: Object.keys(steps)
      });
      if (emitStepAnalytics && !manualStepLoaded.includes(stripStepMarker(String(startingStep)))) {
        const stepName = stripStepMarker(String(startingStep));
        const stepOrderMap = getVisibleStepOrderMap(stepOrder as Array<string | number>, stepOrderOffset, hiddenSteps);
        markStepLoaded(stepName);
        analytics.track('StepLoaded', {
          step: stepName,
          ...(stepOrderMap[stepName] ? { stepOrder: stepOrderMap[stepName] } : {}),
          ...(flowType ? { flowType } : {}),
        });
      }
      hasTrackedFirstStep.current = true;
    }
  }, [startingStep]);

  const handleStepChange = (step: keyof T) => {
    // StepChange fires on every transition. StepLoaded is skipped for $ pass-through steps and for
    // manualStepLoaded steps (which emit their own, e.g. steps that can self-skip). Direction uses the
    // full step order, falling back to the last in-order step for off-order screens (e.g. APP_REVIEW).
    const fromStep = getCurrentStep();

    const fromStepName = stripStepMarker(String(fromStep));
    const toStepName = stripStepMarker(String(step));
    const visibleOrder = getVisibleSteps(stepOrder, hiddenSteps).map((s) => stripStepMarker(String(s)));
    const rawOrder = stepOrder.map((s) => stripStepMarker(String(s)));
    const rawIdx = (stepName: string) => rawOrder.indexOf(stepName);
    const fromIdx = visibleOrder.indexOf(fromStepName);
    const toIdx = visibleOrder.indexOf(toStepName);
    const fromRawIdx = rawIdx(fromStepName);
    const toRawIdx = rawIdx(toStepName);
    const previousRawIdx = previous === null ? -1 : rawIdx(stripStepMarker(String(previous)));
    const fromStepOrder = fromIdx === -1 ? undefined : fromIdx + 1 + stepOrderOffset;
    const toStepOrder = toIdx === -1 ? undefined : toIdx + 1 + stepOrderOffset;
    let direction: 'forward' | 'backward' | 'same' | 'unknown' = 'unknown';
    if (fromRawIdx !== -1 && toRawIdx !== -1) {
      direction = toRawIdx > fromRawIdx ? 'forward' : toRawIdx < fromRawIdx ? 'backward' : 'same';
    } else if (previousRawIdx !== -1 && toRawIdx !== -1) {
      direction = toRawIdx > previousRawIdx ? 'forward' : toRawIdx < previousRawIdx ? 'backward' : 'same';
    }

    // Left a step that never loaded (self-skipped): tag it so funnel widgets can exclude it.
    const fromStepSkipped = emitStepAnalytics && !!fromStepOrder && !loadedSteps.current.has(fromStepName);

    const funnelFields = emitStepAnalytics
      ? {
          step: fromStepName,
          ...(fromStepOrder && !fromStepSkipped ? { stepOrder: fromStepOrder } : {}),
          ...(fromStepSkipped ? { skipped: true } : {}),
          ...(flowType ? { flowType } : {}),
          fromStepName,
          toStepName,
          transition: `${fromStepName}->${toStepName}`,
          direction,
        }
      : {};

    analytics.track('StepChange', {
      fromStep,
      toStep: step,
      ...funnelFields,
      allPlannedSteps: stepOrder,
      allPossibleSteps: Object.keys(steps),
    });

    if (emitStepAnalytics && !String(step).includes('$') && !manualStepLoaded.includes(toStepName)) {
      markStepLoaded(toStepName);
      analytics.track('StepLoaded', {
        step: toStepName,
        ...(toStepOrder ? { stepOrder: toStepOrder } : {}),
        ...(flowType ? { flowType } : {}),
        direction,
      });
    }

    setCurrentStep(step);
  };
  
  const [previous, setPreviousStep] = useState<keyof T | null>(null);
  const cleanStep = (typeof currentStep === 'string' && currentStep.startsWith('$')) ? currentStep.slice(1) as keyof T : currentStep;
  const goToStep = (step: keyof T) => {
    arrivalDirection.current = 'forward';
    stepOrder.indexOf(currentStep) !== -1 && setPreviousStep(currentStep);
    handleStepChange(step);
    onStepChange()
  };
  const nextStep = () => { arrivalDirection.current = 'forward'; findStep('next'); onStepChange(); };
  const previousStep = () => { arrivalDirection.current = 'backward'; findStep('previous'); onStepChange(); };
  const skipStep = () => {
    const canContinueBackward = arrivalDirection.current === 'backward'
      && findTargetIndex(stepOrder, stepOrder.indexOf(currentStep), 'previous', hiddenSteps) !== -1;
    if (canContinueBackward) previousStep();
    else nextStep();
  };
  const goToFirstStep = () => { arrivalDirection.current = 'forward'; handleStepChange(stepOrder[0]); onStepChange(); };
  const goToLastStep = () => { arrivalDirection.current = 'forward'; handleStepChange(stepOrder[stepOrder.length - 1]); onStepChange(); };
  const getCurrentStep = (): keyof T => cleanStep
  const getStepData = (): T[keyof T] => steps[cleanStep];
  const getTotalSteps = (): number => stepOrder.length;
  const getCurrentStepIndex = (): number => calcStepIndex(stepOrder as string[], currentStep as string, hiddenSteps);
  const isFirstStep = (): boolean => getCurrentStepIndex() === 0;
  const isLastStep = (): boolean => getCurrentStepIndex() === stepOrder.length - 1;
  const findStep = (direction: 'next' | 'previous') => {
    const currentIndex = stepOrder.indexOf(currentStep);
    const increment = direction === 'next' ? 1 : -1;
    if (currentIndex !== -1) {
      // $-prefixed steps are invisible pass-through effects (associate, submit, ...): nextStep visits
      // them so they run, but backward navigation skips them so the user never lands on one. Hidden
      // steps are passed over in both directions (see wizardNavigation.ts).
      const newIndex = findTargetIndex(stepOrder, currentIndex, direction, hiddenSteps);
      if (newIndex !== -1) {
        handleStepChange(stepOrder[newIndex]);
        return;
      }
    } else if (direction === 'previous' && previous !== null) {
      handleStepChange(previous);
      return;
    } else if (direction == 'next' && previous !== null) {
      const nextIndex = stepOrder.indexOf(previous) + 1;
      nextIndex < stepOrder.length && handleStepChange(stepOrder[nextIndex]);
      return;
    }

    const stepKeys = Object.keys(steps) as Array<keyof T>;
    const stepIndex = stepKeys.indexOf(currentStep);
    if (stepIndex !== -1 && stepIndex + increment >= 0 && stepIndex + increment < stepKeys.length) {
      handleStepChange(stepKeys[stepIndex + increment]);
    }
  };
  return { getCurrentStep, goToStep, nextStep, getStepData, getTotalSteps, getCurrentStepIndex, previousStep, isFirstStep, isLastStep, goToFirstStep, goToLastStep, skipStep, markStepLoaded, stepOrder };
}

export type StepOrder<T> = Array<(Extract<keyof T, string | number>) | `$${Extract<keyof T, string | number>}`>

interface WizardProps<T extends WizardSteps> {
  initialStep?: keyof T;
  steps?: T;
  stepOrder?: StepOrder<T>;
  progressOffset?: number;
  stepOrderOffset?: number;
  onStepChange?: () => void;
  showProgressBar?: boolean;
  emitStepAnalytics?: boolean;
  flowType?: string;
  manualStepLoaded?: string[];
  /**
   * Steps in `stepOrder` the applicant will not see right now (e.g. PROVE_STEP once consent is written).
   * They are passed over in both directions, never mount, and do not count in the progress bar or in
   * isFirstStep(). The parent derives this from live data each render.
   */
  hiddenSteps?: string[];
  children?: React.ReactNode;
}

export function Wizard<T extends WizardSteps>({ initialStep, steps = {} as T, stepOrder = [], progressOffset = 0, stepOrderOffset = 0, showProgressBar = true, onStepChange, emitStepAnalytics = false, flowType, manualStepLoaded = [], hiddenSteps = [], children }: WizardProps<T>) {
  const wizard = useWizard(initialStep, steps, stepOrder, onStepChange, emitStepAnalytics, flowType, stepOrderOffset, manualStepLoaded, hiddenSteps);
  const stepIndex = wizard.getCurrentStepIndex();
  return (
    <WizardContext.Provider value={wizard as WizardContextProps<T>}>
      <Stack gap={5}>
        {showProgressBar && <SteppedRange steps={stepOrder.filter((step) => !isHiddenStep(step, hiddenSteps)) as string[]} stepIndex={stepIndex < 0 ? -1 : stepIndex} progressOffset={progressOffset} />}
        {wizard.getStepData()}
        {children}
      </Stack>
    </WizardContext.Provider>
  );
}

export const useWizardContext = <T extends WizardSteps>() => {
  const context = useContext(WizardContext) as WizardContextProps<T> | undefined;
  if (!context) {
    throw new Error('useWizardContext must be used within a WizardProvider');
  }
  return context;
};
